import type { FlyerExtracted } from '@onesunday/api-client';

/** Normalise a single matched time token to HH:MM (24-hour). */
export const to24 = (raw: string): string => {
  const lower = raw.toLowerCase().replace(/\s/g, '');
  const isPm = lower.endsWith('pm');
  const isAm = lower.endsWith('am');
  const digits = lower.replace(/[^\d:h]/g, '');
  const [hStr, mStr = '00'] = digits.split(/[:h]/);
  let h = parseInt(hStr, 10);
  if (isPm && h < 12) h += 12;
  if (isAm && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${mStr.padStart(2, '0')}`;
};

/**
 * Parse raw OCR text into structured flyer fields using regex heuristics.
 * Tuned for South African event flyers.
 */
export const parseFlyerText = (text: string): FlyerExtracted => {
  // ── Address: street number + name + SA street type ──────────────────────────
  const ADDRESS_RE =
    /\b\d+\s+[A-Za-z][A-Za-z\s]{2,35}(?:Avenue|Ave|Street|St|Road|Rd|Drive|Dr|Close|Cl|Crescent|Cres|Place|Pl|Boulevard|Blvd|Lane|Ln|Way|Terrace|Tce|Circuit|Crt)\b[^\n]*/i;
  const addressMatch = text.match(ADDRESS_RE);

  // ── SA phone: 0xx xxx xxxx or 0xx-xxx-xxxx ──────────────────────────────────
  const PHONE_RE = /\b0[6-8]\d[\s.-]?\d{3}[\s.-]?\d{4}\b/;
  const phoneMatch = text.match(PHONE_RE);

  // ── Hours: "12pm – 10pm", "12:00 - 22:00", "12h00 to 22h00" ────────────────
  const TIME_TOKEN = /\d{1,2}(?::\d{2}|h\d{2})?\s*(?:am|pm)?/i.source;
  const HOURS_RE = new RegExp(`(${TIME_TOKEN})\\s*[-–—]\\s*(${TIME_TOKEN})`, 'i');
  const hoursMatch = text.match(HOURS_RE);
  const eventHours = hoursMatch
    ? `${to24(hoursMatch[1])}–${to24(hoursMatch[2])}`
    : null;

  // ── Date: "Saturday 3 May 2026" or "3 May 2026" ─────────────────────────────
  const MONTHS = 'January|February|March|April|May|June|July|August|September|October|November|December';
  const DAYS   = 'Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday';
  const DATE_RE = new RegExp(
    `(?:(?:${DAYS})\\s+)?\\d{1,2}\\s+(?:${MONTHS})\\s+\\d{4}`,
    'i'
  );
  const dateMatch = text.match(DATE_RE);

  // ── Venue name: the non-date/time line immediately above the address ─────────
  let venueName: string | null = null;
  if (addressMatch) {
    const idx = text.indexOf(addressMatch[0]);
    const before = text.slice(0, idx).trimEnd();
    const prevLine = before.split('\n').filter(Boolean).pop()?.trim() ?? null;
    if (prevLine && prevLine.length > 4 && prevLine.length < 60 && !/\d{4}/.test(prevLine)) {
      venueName = prevLine;
    }
  }

  return {
    address:       addressMatch ? addressMatch[0].replace(/\s+/g, ' ').trim() : null,
    venueName,
    eventHours,
    eventDate:     dateMatch ? dateMatch[0].trim() : null,
    contactNumber: phoneMatch ? phoneMatch[0].replace(/[\s.-]/g, ' ').trim() : null,
  };
};

/**
 * Run Tesseract OCR on a flyer image and return structured fields.
 * Never throws — returns all-null on any failure.
 */
export const extractFlyerInfoOcr = async (file: File): Promise<FlyerExtracted> => {
  const empty: FlyerExtracted = {
    address: null, venueName: null, eventHours: null,
    eventDate: null, contactNumber: null,
  };
  try {
    const { recognize } = await import('tesseract.js');
    const { data: { text } } = await recognize(file, 'eng');
    return parseFlyerText(text);
  } catch {
    return empty;
  }
};
